import { Stats } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import React, { ReactElement, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import WoltStar from 'common/components/icons/WoltStar';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { useExperienceModuleContext } from 'modules/experience/context';

import { NavigationItem, NavigationKey } from '../../types';

type NavigationProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
  businessNavigationItems: NavigationItem[];
  venueNavigationItems: NavigationItem[];
};

const NavigationContainer = styled.nav<{ isSidebarOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-self: stretch;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;

  /*** scrollbar customization ***/
  scrollbar-width: ${({ isSidebarOpen }) => (isSidebarOpen ? 'thin' : 'none')};
`;

const NavigationSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  border-radius: 8px;
  gap: 8px;
  width: 100%;
`;

const NavigationSectionHeader = styled.div`
  display: flex;
  align-self: stretch;
  height: 20px;
  padding: 2px 8px;
  border-radius: 8px;
  gap: 8px;
`;

const NavigationSectionTitle = styled(Typography.Small)`
  color: ${({ theme }) => theme.cbSemanticColors.textSecondary};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: 2px;
`;

const IconProps = {
  width: 16,
  height: 16,
  fill: 'currentColor',
};

const StyledMenuActionItem = styled(MenuActionItem)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-block: 8px;
  transition: background-color 150ms ease-in-out;
`;

const IconsMappings: Record<NavigationKey, ReactElement> = {
  [NavigationKey.INSIGHTS]: <Stats {...IconProps} />,
  [NavigationKey.BEST_OF_WOLT]: <WoltStar {...IconProps} />,
};

const NavigationSection = ({
  header,
  isSidebarOpen,
  setIsSidebarOpen,
  navigationItems,
}: {
  header: ReactNode;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
  navigationItems: NavigationItem[];
}) => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const { url: baseUrl } = useRouteMatch();
  const { activeNavigation, setActiveNavigation } = useExperienceModuleContext();
  const navigate = (item: NavigationItem) => {
    history.push(`${baseUrl}${item.link}`);
    setActiveNavigation(item);
    isMobile && setIsSidebarOpen(false);
  };

  return (
    <NavigationSectionContainer>
      {isSidebarOpen && (
        <NavigationSectionHeader>
          <NavigationSectionTitle>{header}</NavigationSectionTitle>
        </NavigationSectionHeader>
      )}
      {navigationItems.map(item => (
        <StyledMenuActionItem
          key={item.key}
          variant="black"
          selected={activeNavigation?.key === item.key}
          leftContent={<IconContainer>{IconsMappings[item.key]}</IconContainer>}
          onClick={() => navigate(item)}
        >
          {isSidebarOpen && (
            <Typography.Body3 fontWeight="normal">
              <FormattedMessage id={item.intlKey} />
            </Typography.Body3>
          )}
        </StyledMenuActionItem>
      ))}
    </NavigationSectionContainer>
  );
};

export const Navigation = ({
  isSidebarOpen,
  setIsSidebarOpen,
  businessNavigationItems,
  venueNavigationItems,
}: NavigationProps) => {
  return (
    <NavigationContainer isSidebarOpen={isSidebarOpen}>
      {businessNavigationItems.length > 0 && (
        <NavigationSection
          header={<FormattedMessage id="experience.merchant" />}
          isSidebarOpen={isSidebarOpen}
          navigationItems={businessNavigationItems}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      )}
      {venueNavigationItems.length > 0 && (
        <NavigationSection
          header={<FormattedMessage id="experience.venue" />}
          isSidebarOpen={isSidebarOpen}
          navigationItems={venueNavigationItems}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      )}
    </NavigationContainer>
  );
};
